import React, { useCallback, useEffect, useRef, useState } from "react";
import { Building2, CheckCircle2, Circle, Plus, Trash2, UserCheck } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ClientViewButton } from "../components/ClientView";
import { ActionIcon } from "../components/ActionIcon";
import { CopyId } from "../components/CopyId";

export default function AdminCompanies() {
  const [companies, setCompanies] = useState([]);
  const [companySearch, setCompanySearch] = useState("");
  const [users, setUsers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [active, setActive] = useState("");
  const [newCompany, setNewCompany] = useState("");
  const [catalog, setCatalog] = useState([]);
  const [config, setConfig] = useState({ program_start: "", group_policy_number: "", surplus_or_errs: "ERRs", short_name: "" });
  const [savingConfig, setSavingConfig] = useState(false);
  const detailRef = useRef(null);

  const manage = (name) => {
    setActive(name);
    requestAnimationFrame(() => detailRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
  };

  const load = useCallback(async () => {
    const [c, u] = await Promise.all([api.get("/admin/companies"), api.get("/admin/users")]);
    setCompanies(c.data);
    setUsers(u.data);
    setActive((current) => current || c.data[0]?.name || "");
  }, []);

  const loadCatalog = useCallback(async () => {
    const { data } = await api.get("/admin/vendor-catalog");
    setCatalog(data.map((v) => v.name));
  }, []);

  const loadVendors = useCallback(async (company) => {
    if (!company) return setVendors([]);
    const { data } = await api.get("/admin/vendors", { params: { company } });
    setVendors(data);
  }, []);

  useEffect(() => { load().catch((e) => toast.error(formatApiError(e.response?.data?.detail))); }, [load]);
  useEffect(() => { loadCatalog().catch(() => setCatalog([])); }, [loadCatalog]);
  useEffect(() => {
    const company = companies.find((c) => c.name === active);
    setConfig({
      program_start: company?.program_start || "",
      group_policy_number: company?.group_policy_number || "",
      surplus_or_errs: company?.surplus_or_errs || "ERRs",
      short_name: company?.short_name || "",
    });
  }, [active, companies]);
  useEffect(() => { loadVendors(active).catch(() => setVendors([])); }, [active, loadVendors]);

  const addCompany = async (event) => {
    event.preventDefault();
    try {
      await api.post("/admin/companies", { name: newCompany });
      toast.success(`${newCompany} added`);
      setActive(newCompany.trim());
      setNewCompany("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const removeCompany = async (name) => {
    if (!window.confirm(`Delete ${name}? This also removes its financial feed data, census and vendor list.`)) return;
    try {
      await api.delete(`/admin/companies/${encodeURIComponent(name)}`);
      toast.success("Company deleted");
      setActive("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveVendorList = async (names, message) => {
    try {
      await api.put(`/admin/companies/${encodeURIComponent(active)}/vendors`, { vendors: names });
      toast.success(message);
      loadVendors(active);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const assignVendor = (name) =>
    saveVendorList([...vendors.map((v) => v.name), name], `${name} assigned to ${active}`);

  const cancelInvite = async (member) => {
    if (!window.confirm(`Cancel the pending invitation for ${member.name}? The emailed link stops working.`)) return;
    try {
      await api.delete(`/admin/users/${member.id}/invite`);
      toast.success("Invitation cancelled");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const resendInvite = async (member) => {
    try {
      const { data } = await api.post(`/admin/users/${member.id}/invite`);
      toast.success(`Invitation re-sent to ${data.sent_to}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveConfig = async () => {
    setSavingConfig(true);
    try {
      await api.put(`/admin/companies/${encodeURIComponent(active)}/settings`, {
        invoice_service: activeCompany?.invoice_service !== false,
        ...config,
      });
      toast.success("Employer configuration saved");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingConfig(false); }
  };

  const setInvoiceService = async (company, enabled) => {
    try {
      await api.put(`/admin/companies/${encodeURIComponent(company.name)}/settings`,
        { invoice_service: enabled });
      toast.success(`Invoice payment service turned ${enabled ? "on" : "off"} for ${company.name}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const removeVendor = (vendor) =>
    saveVendorList(vendors.filter((v) => v.name !== vendor.name).map((v) => v.name),
      `${vendor.name} removed from ${active}`);

  const updateUser = async (user, patch, message) => {
    try {
      const { data } = await api.put(`/admin/users/${user.id}`, patch);
      setUsers((current) => current.map((u) => (u.id === user.id ? data : u)));
      toast.success(message);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const customers = users.filter((u) => u.role === "customer");
  const term = companySearch.trim().toLowerCase();
  const visibleCompanies = term
    ? companies.filter((c) => c.name.toLowerCase().includes(term) || (c.company_id || "").toLowerCase().includes(term))
    : companies;
  const activeCompany = companies.find((c) => c.name === active) || null;
  const unassignedVendors = catalog.filter((name) => !vendors.some((v) => v.name === name));
  const members = customers.filter((u) => u.company === active);
  const unassigned = customers.filter((u) => u.company !== active);

  return (
    <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-10" data-testid="admin-companies-page">
      <div className="mb-8">
        <div className="eyebrow mb-2">Administration</div>
        <h1 className="font-heading font-black text-4xl sm:text-5xl leading-none tracking-tight" style={{ color: "var(--ink)" }}>
          Employers
        </h1>
        <p className="mt-3 text-base max-w-2xl" style={{ color: "var(--muted)" }}>
          Create employers, assign their users, designate who approves invoices, and control the
          vendor list each employer can submit invoices against.
        </p>
      </div>

      <form onSubmit={addCompany} className="border p-6 mb-8 flex flex-col sm:flex-row gap-4 sm:items-end" style={{ borderColor: "var(--border)", background: "var(--surface)" }} data-testid="add-company-form">
        <div className="flex-1">
          <label className="field-label">New employer</label>
          <input className="input-flat" required minLength={2} value={newCompany} onChange={(e) => setNewCompany(e.target.value)} placeholder="e.g. Northwind Logistics" data-testid="new-company-name"/>
        </div>
        <button type="submit" className="btn-primary flex items-center gap-2" data-testid="add-company-submit">
          <Plus size={14}/> Add Employer
        </button>
      </form>

      <div className="flex items-center justify-end mb-3">
        <input className="input-flat max-w-xs" placeholder="Search employer or Company ID" value={companySearch}
               aria-label="Search employers by name or Company ID" onChange={(e) => setCompanySearch(e.target.value)} data-testid="companies-search" />
      </div>

      <div className="border max-h-[420px] overflow-auto mb-8" style={{ borderColor: "var(--border)", background: "var(--surface)" }} data-testid="companies-table-region">
        <table className="w-full text-sm min-w-[640px]" data-testid="companies-table">
          <thead>
            <tr className="border-b eyebrow text-left" style={{ borderColor: "var(--border)" }}>
              <th className="px-4 py-3">Employer</th>
              <th className="px-4 py-3 whitespace-nowrap">Company ID</th>
              <th className="px-4 py-3 text-right">Users</th>
              <th className="px-4 py-3 text-right">Approvers</th>
              <th className="px-4 py-3 text-right">Vendors</th>
              <th className="px-4 py-3">Setup</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {visibleCompanies.map((company) => (
              <tr key={company.name} className="border-b" style={{ borderColor: "var(--border)", background: active === company.name ? "#F3F3F1" : "transparent" }} data-testid={`company-row-${company.name.replace(/\s+/g, "-").toLowerCase()}`}>
                <td className="px-4 py-3 font-medium" style={{ color: "var(--ink)" }}>
                  <button onClick={() => manage(company.name)} className="flex items-center gap-2 hover:underline">
                    <Building2 size={14} style={{ color: "var(--muted)" }}/> {company.name}
                  </button>
                </td>
                <td className="px-4 py-3">
                  <CopyId value={company.company_id} testId={`company-id-${company.name.replace(/\s+/g, "-").toLowerCase()}`} />
                </td>
                <td className="px-4 py-3 text-right" style={{ color: "var(--muted)" }}>{company.users}</td>
                <td className="px-4 py-3 text-right" style={{ color: company.approvers ? "var(--ink)" : "#9B2C1C" }}>
                  {company.approvers || "None"}
                </td>
                <td className="px-4 py-3 text-right" style={{ color: "var(--muted)" }}>{company.vendors}</td>
                <td className="px-4 py-3" data-testid={`setup-gaps-${company.name.replace(/\s+/g, "-").toLowerCase()}`}>
                  {company.gaps?.length ? (
                    <span className="flex flex-wrap gap-1.5">
                      {company.gaps.map((gap) => (
                        <span key={gap} className="pill text-[10px]" style={{ borderColor: "#B4472D", color: "#9B2C1C" }}>{gap}</span>
                      ))}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium" style={{ color: "var(--accent)" }}>
                      <CheckCircle2 size={13}/> Ready
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <span className="inline-flex items-center gap-2 justify-end">
                    <ClientViewButton company={company.name} />
                    <button
                      onClick={() => manage(company.name)}
                      className="btn-ghost !px-2 !py-1 text-xs"
                      style={active === company.name ? { background: "var(--ink)", color: "var(--surface)" } : undefined}
                      data-testid={`manage-${company.name.replace(/\s+/g, "-").toLowerCase()}`}
                    >
                      {active === company.name ? "Managing" : "Manage"}
                    </button>
                    <ActionIcon
                      icon={Trash2}
                      label="Delete employer and its program data"
                      onClick={() => removeCompany(company.name)}
                      testId={`delete-company-${company.name.replace(/\s+/g, "-").toLowerCase()}`}
                    />
                  </span>
                </td>
              </tr>
            ))}
            {!companies.length && <tr><td colSpan={7} className="p-10 text-center text-sm" style={{ color: "var(--muted)" }} data-testid="companies-empty">No employers yet.</td></tr>}
            {Boolean(companies.length) && !visibleCompanies.length && <tr><td colSpan={7} className="p-10 text-center text-sm" style={{ color: "var(--muted)" }} data-testid="companies-no-match">No employer matches that search.</td></tr>}
          </tbody>
        </table>
      </div>

      {Boolean(companies.length) && (
        <div ref={detailRef} className="grid grid-cols-1 lg:grid-cols-2 gap-8 scroll-mt-24" data-testid="company-detail">
          <div className="border" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
            <div className="p-5 border-b" style={{ borderColor: "var(--border)" }}>
              <div className="eyebrow">Configuration</div>
              <div className="font-heading font-bold text-xl mt-1" style={{ color: "var(--ink)" }}>{active || "Select a company"}</div>
              {activeCompany && (
                <label className="mt-4 flex items-start gap-3 text-sm" style={{ color: "var(--ink)" }}>
                  <Checkbox
                    className="mt-0.5"
                    checked={activeCompany.invoice_service !== false}
                    onCheckedChange={(value) => setInvoiceService(activeCompany, Boolean(value))}
                    aria-label={`Invoice payment service for ${activeCompany.name}`}
                    data-testid={`invoice-service-${activeCompany.name.replace(/\s+/g, "-").toLowerCase()}`}
                  />
                  <span>
                    Invoice payment service
                    <span className="block text-xs" style={{ color: "var(--muted)" }}>
                      When off, this client sees no invoice menus, approvals or vendor payment charts.
                    </span>
                  </span>
                </label>
              )}
              {activeCompany && (
                <div className="mt-5 border-t pt-4 space-y-4" data-testid="employer-config-fields" style={{ borderColor: "var(--border)" }}>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <div>
                    <label className="field-label">Company ID (system)</label>
                    <div className="input-flat flex items-center" style={{ background: "#F3F3F1" }} data-testid="employer-company-id">
                      <CopyId value={activeCompany.company_id} testId="employer-company-id" />
                    </div>
                    </div>
                    <div>
                      <label className="field-label">Group policy number</label>
                      <input className="input-flat" value={config.group_policy_number} onChange={(e) => setConfig({ ...config, group_policy_number: e.target.value })} data-testid="employer-group-policy"/>
                    </div>
                    <div>
                      <label className="field-label">Program start date</label>
                      <input className="input-flat" type="date" value={config.program_start} onChange={(e) => setConfig({ ...config, program_start: e.target.value })} data-testid="employer-program-start"/>
                    </div>
                  </div>
                  <div>
                    <label className="field-label">Company short name</label>
                    <input className="input-flat" value={config.short_name} onChange={(e) => setConfig({ ...config, short_name: e.target.value })} placeholder="Used where space is tight" data-testid="employer-short-name"/>
                  </div>
                  <div>
                    <label className="field-label">Wording for reports</label>
                    <div className="flex gap-6 mt-1">
                      {["ERRs", "Surplus"].map((option) => (
                        <label key={option} className="flex items-center gap-2 text-sm" style={{ color: "var(--ink)" }}>
                          <input
                            type="radio"
                            name="surplus-or-errs"
                            checked={config.surplus_or_errs === option}
                            onChange={() => setConfig({ ...config, surplus_or_errs: option })}
                            data-testid={`employer-wording-${option.toLowerCase()}`}
                          />
                          {option}
                        </label>
                      ))}
                    </div>
                  </div>
                  <button type="button" onClick={saveConfig} disabled={savingConfig} className="btn-primary w-full" data-testid="employer-config-save">
                    {savingConfig ? "Saving…" : "Save Configuration"}
                  </button>
                </div>
              )}
              {activeCompany && (
                <div className="mt-5 border-t pt-4" data-testid="employer-checklist" style={{ borderColor: "var(--border)" }}>
                  <div className="eyebrow mb-3">Onboarding checklist</div>
                  <ul className="grid gap-3 text-sm sm:grid-cols-2 xl:grid-cols-3">
                    {[
                      { label: "At least one user", done: activeCompany.users > 0, hint: "Invite a member below" },
                      ...(activeCompany.invoice_service !== false ? [
                        { label: "An approver designated", done: activeCompany.approvers > 0, hint: "Tick Approver on a member" },
                        { label: "Vendors assigned", done: activeCompany.vendors > 0, hint: "Assign from the vendor catalog" },
                      ] : []),
                      { label: "Census uploaded", done: Boolean(activeCompany.census_loaded), hint: "Upload under Setup › Census" },
                      { label: "Financial feed loaded", done: Boolean(activeCompany.feed_loaded), hint: "Ingest the monthly workbook under Setup › Financials" },
                    ].map((item) => (
                      <li key={item.label} className="flex items-start gap-2.5" data-testid={`checklist-${item.label.replace(/\s+/g, "-").toLowerCase()}`}>
                        {item.done
                          ? <CheckCircle2 size={15} className="mt-0.5 shrink-0" style={{ color: "var(--accent)" }}/>
                          : <Circle size={15} className="mt-0.5 shrink-0" style={{ color: "#B4472D" }}/>}
                        <span style={{ color: item.done ? "var(--muted)" : "var(--ink)" }}>
                          {item.label}
                          {!item.done && (
                            <span className="block text-xs" style={{ color: "var(--muted)" }}>{item.hint}</span>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div className="p-5 border-b" style={{ borderColor: "var(--border)" }}>
              <div className="eyebrow">Members & approvers</div>
              <p className="mt-2 text-sm" style={{ color: "var(--muted)" }}>
                Approvers can approve or reject their company's invoices. Without one, invoices wait for
                an approver to be designated.
              </p>
            </div>
            <div className="divide-y max-h-[360px] overflow-y-auto" style={{ borderColor: "var(--border)" }} data-testid="company-members">
              {members.map((member) => (
                <div key={member.id} className="flex items-center justify-between gap-4 px-5 py-4" data-testid={`member-${member.username}`}>
                  <div>
                    <div className="font-medium" style={{ color: "var(--ink)" }}>{member.name}</div>
                    <div className="text-xs" style={{ color: "var(--muted)" }}>{member.username} · {member.work_email}</div>
                    {member.invite_pending && (
                      <div className="text-xs mt-1 flex items-center gap-2" style={{ color: "#9B2C1C" }} data-testid={`member-invite-pending-${member.username}`}>
                        Invitation pending
                        <button
                          type="button"
                          onClick={() => cancelInvite(member)}
                          className="underline"
                          data-testid={`member-cancel-invite-${member.username}`}
                        >
                          Cancel invitation
                        </button>
                        <button
                          type="button"
                          onClick={() => resendInvite(member)}
                          className="underline"
                          data-testid={`member-resend-invite-${member.username}`}
                        >
                          Resend
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-4">
                  <Select
                    value={member.company}
                    onValueChange={(name) => {
                      if (name !== member.company) {
                        updateUser(member, { company: name }, `${member.name} moved to ${name}`);
                      }
                    }}
                  >
                    <SelectTrigger className="!rounded-none h-8 text-xs min-w-[180px]" data-testid={`member-company-${member.username}`}>
                      <SelectValue placeholder="Company" />
                    </SelectTrigger>
                    <SelectContent className="!rounded-none max-h-72">
                      {companies.map((company) => (
                        <SelectItem key={company.name} value={company.name} className="text-xs">
                          {company.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {activeCompany?.invoice_service !== false && (
                  <label className="flex items-center gap-2 text-xs whitespace-nowrap" style={{ color: "var(--ink)" }}>
                    <Checkbox
                      checked={member.is_approver}
                      onCheckedChange={(value) => updateUser(member, { is_approver: Boolean(value) },
                        value ? `${member.name} can now approve invoices` : `${member.name} can no longer approve invoices`)}
                      aria-label={`${member.name} approves invoices`}
                      data-testid={`approver-${member.username}`}
                    />
                    <UserCheck size={13}/> Approver
                  </label>
                  )}
                  </div>
                </div>
              ))}
              {!members.length && (
                <div className="p-8 text-center text-sm" style={{ color: "var(--muted)" }} data-testid="company-members-empty">
                  No users assigned to this employer yet.
                </div>
              )}
            </div>
            {Boolean(unassigned.length) && active && (
              <div className="p-5 border-t" style={{ borderColor: "var(--border)" }}>
                <label className="field-label">Assign an existing user</label>
                <Select value="" onValueChange={(id) => {
                  const target = customers.find((u) => u.id === id);
                  if (target) updateUser(target, { company: active }, `${target.name} moved to ${active}`);
                }}>
                  <SelectTrigger className="!rounded-none !border-x-0 !border-t-0 !bg-transparent !px-0" data-testid="assign-user-select">
                    <SelectValue placeholder="Choose A User"/>
                  </SelectTrigger>
                  <SelectContent className="!rounded-none max-h-72">
                    {unassigned.map((u) => (
                      <SelectItem key={u.id} value={u.id}>{u.name} — {u.company || "unassigned"}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {activeCompany?.invoice_service !== false ? (
          <div className="border" style={{ borderColor: "var(--border)", background: "var(--surface)" }} data-testid="vendor-card">
            <div className="p-5 border-b" style={{ borderColor: "var(--border)" }}>
              <div className="eyebrow">Vendor list</div>
              <div className="font-heading font-bold text-xl mt-1" style={{ color: "var(--ink)" }}>
                Vendors for {active || "—"}
              </div>
              <p className="mt-2 text-sm" style={{ color: "var(--muted)" }}>
                Only these vendors appear in this employer's new invoice form.
              </p>
            </div>
            <div className="p-5 border-b" style={{ borderColor: "var(--border)" }} data-testid="assign-vendor-block">
              <label className="field-label">Assign a vendor from the catalog</label>
              <Select value="" onValueChange={assignVendor}>
                <SelectTrigger className="!rounded-none !border-x-0 !border-t-0 !bg-transparent !px-0" disabled={!active} data-testid="assign-vendor-select">
                  <SelectValue placeholder={unassignedVendors.length ? "Choose A Vendor" : "All catalog vendors assigned"}/>
                </SelectTrigger>
                <SelectContent className="!rounded-none max-h-72">
                  {unassignedVendors.map((name) => <SelectItem key={name} value={name}>{name}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="mt-2 text-xs" style={{ color: "var(--muted)" }}>
                Pick one at a time. Manage the master list under the Vendors menu.
              </p>
            </div>
            <div className="divide-y max-h-[360px] overflow-y-auto" style={{ borderColor: "var(--border)" }} data-testid="vendor-list">
              {vendors.map((vendor) => (
                <div key={vendor.id} className="flex items-center justify-between px-5 py-3 text-sm" data-testid={`vendor-${vendor.id}`}>
                  <span style={{ color: "var(--ink)" }}>{vendor.name}</span>
                  <ActionIcon icon={Trash2} label="Remove vendor from this employer" onClick={() => removeVendor(vendor)} testId={`delete-vendor-${vendor.id}`}/>
                </div>
              ))}
              {!vendors.length && (
                <div className="p-8 text-center text-sm" style={{ color: "var(--muted)" }} data-testid="vendor-list-empty">
                  No vendors yet — this employer cannot submit invoices until you assign at least one.
                </div>
              )}
            </div>
          </div>
          ) : (
            <div className="border p-8 text-sm" style={{ borderColor: "var(--border)", background: "var(--surface)", color: "var(--muted)" }} data-testid="vendor-card-disabled">
              This employer does not use the invoice payment service, so it has no vendor list and no
              approvers.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
